/**
 * AppShell — the persistent shell (layout route). Sidebar is derived from the
 * unified nav config (sectionsForRole), so it stays in sync with breadcrumb +
 * routing. Renders the active page via <Outlet/>.
 *
 * Machinery forked from PayPilot AdminLayout: collapsible sections, icon-rail,
 * mobile drawer, "/" menu search, sticky top bar with breadcrumb.
 */
import { useState, useEffect, useMemo, useRef, Suspense } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Link, Outlet, useLocation, useNavigate } from 'react-router-dom';
import {
  Search,
  Menu,
  X,
  ChevronDown,
  ChevronRight,
  ChevronLeft,
  LogOut,
  PanelLeftClose,
  PanelLeft,
  Grid3x3,
} from 'lucide-react';
import { useAuth } from '../hooks/useAuth';
import { logout as liffLogout } from '../lib/liff';
import {
  sectionsForRole,
  findNavGroup,
  SIDEBAR_SECTIONS,
  UI_MODE_KEY,
  NAV_COLLAPSE_KEY,
  NAV_RAIL_KEY,
  type UiMode,
  type NavItem,
} from '../nav/config';
import { Icon } from './Icon';
import { Logo } from './ui/Logo';
import { NotificationBell } from './NotificationBell';
import { TenantSwitcher } from './TenantSwitcher';
import { Skeleton } from './ui/Skeleton';
import { SubNav } from './SubNav';
import { ErrorBoundary } from './ErrorBoundary';
import { hrDelegationApi } from '../api';

function lsGet<T>(key: string, fallback: T): T {
  try {
    const raw = localStorage.getItem(key);
    return raw ? (JSON.parse(raw) as T) : fallback;
  } catch {
    return fallback;
  }
}
function lsSet(key: string, val: unknown) {
  try {
    localStorage.setItem(key, JSON.stringify(val));
  } catch {
    /* ignore */
  }
}

export function AppShell() {
  const location = useLocation();
  const navigate = useNavigate();
  const { profile, role, logout } = useAuth();

  // OWNER-only badge: count of HR requests awaiting approval, shown on the
  // "คำขอรออนุมัติ" menu. Shares the 'pending-changes' query key so the inbox's
  // approve/reject invalidation refreshes the badge too.
  const { data: pendingCount = 0 } = useQuery({
    queryKey: ['pending-changes', 'count'],
    queryFn: () => hrDelegationApi.listPending('pending').then((r) => r.length),
    enabled: role === 'OWNER' && !!profile?.tenant?.id,
    staleTime: 60_000,
  });

  const [drawerOpen, setDrawerOpen] = useState(false);
  const [collapsed, setCollapsed] = useState<Record<string, boolean>>(() => lsGet(NAV_COLLAPSE_KEY, {}));
  const [rail, setRail] = useState<boolean>(() => {
    try {
      return localStorage.getItem(NAV_RAIL_KEY) === '1';
    } catch {
      return false;
    }
  });
  const [mode] = useState<UiMode>(() => {
    try {
      return localStorage.getItem(UI_MODE_KEY) === 'full' ? 'full' : 'simple';
    } catch {
      return 'simple';
    }
  });
  const [query, setQuery] = useState('');
  const searchRef = useRef<HTMLInputElement>(null);

  // "/" focuses the menu search (unless typing in a field).
  useEffect(() => {
    function onKey(e: KeyboardEvent) {
      const tag = (e.target as HTMLElement | null)?.tagName;
      if (e.key === '/' && tag !== 'INPUT' && tag !== 'TEXTAREA') {
        e.preventDefault();
        searchRef.current?.focus();
      }
    }
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);

  // Close the mobile drawer on navigation.
  useEffect(() => {
    setDrawerOpen(false);
  }, [location.pathname]);

  const sections = useMemo(() => sectionsForRole(role, mode), [role, mode]);
  const activeId = location.pathname.replace(/^\/+/, '').split('/')[0] || 'dashboard';

  const allItems = useMemo(() => sections.flatMap((s) => s.items), [sections]);
  const q = query.trim().toLowerCase();
  const filtered = q ? allItems.filter((it) => it.label.toLowerCase().includes(q)) : [];

  // Breadcrumb: แผนผังระบบ › group › page
  const activeItem = SIDEBAR_SECTIONS.flatMap((s) => s.items).find((it) => it.id === activeId);
  const activeGroup = findNavGroup(activeId);

  function toggleGroup(id: string) {
    setCollapsed((prev) => {
      const next = { ...prev, [id]: !prev[id] };
      lsSet(NAV_COLLAPSE_KEY, next);
      return next;
    });
  }
  function toggleRail() {
    setRail((v) => {
      const n = !v;
      try {
        localStorage.setItem(NAV_RAIL_KEY, n ? '1' : '0');
      } catch {
        /* ignore */
      }
      return n;
    });
  }
  function handleLogout() {
    logout();
    try {
      liffLogout();
    } catch {
      /* ignore */
    }
    navigate('/');
  }

  function renderItem(item: NavItem, indent = true) {
    const active = item.id === activeId;
    return (
      <Link
        key={item.id}
        to={`/${item.id}`}
        className={`flex items-center gap-2.5 ${indent ? 'pl-5 pr-3' : 'px-3'} h-9 rounded-lg text-sm transition ${
          active ? 'font-medium bg-brand-50 text-brand-700' : 'text-gray-600 hover:bg-gray-100'
        }`}
      >
        <Icon name={item.icon} className="w-4 h-4 shrink-0" />
        <span className="flex-1 truncate">{item.label}</span>
        {item.id === 'pending-inbox' && pendingCount > 0 && (
          <span className="badge bg-amber-100 text-amber-700 tabular-nums shrink-0">{pendingCount}</span>
        )}
      </Link>
    );
  }

  const sidebar = (
    <aside className="w-64 bg-white border-r border-gray-200 flex flex-col h-screen sticky top-0">
      <div className="px-5 py-4 border-b border-gray-100">
        <Logo wordmark />
        <TenantSwitcher />
        {role === 'OWNER' && (
          // The app switcher lives in PayPilot (the suite's anchor/launcher hub),
          // not inside Eval — a full navigation out of this SPA. The internal
          // /launcher route was never migrated (Placeholder), which trapped users
          // in Eval. Link straight to the PayPilot launcher, same as the vanilla app.
          <a
            href="https://payroll-edge-liff.pages.dev/launcher"
            className="mt-2 flex items-center gap-2 px-2 py-1.5 text-xs text-gray-600 hover:text-brand-700 hover:bg-brand-50 rounded-lg"
          >
            <Grid3x3 className="w-3.5 h-3.5" />
            เปลี่ยนแอป (HumanAI)
          </a>
        )}
      </div>

      <div className="px-3 pt-3">
        <div className="flex items-center gap-2 rounded-lg bg-gray-100 px-2.5 py-1.5">
          <Search className="w-4 h-4 text-gray-400 shrink-0" />
          <input
            ref={searchRef}
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="ค้นหาเมนู…"
            className="bg-transparent text-sm w-full outline-none placeholder:text-gray-400"
          />
          <kbd className="text-[10px] text-gray-400 border border-gray-300 rounded px-1 shrink-0">/</kbd>
        </div>
      </div>

      <nav className="flex-1 overflow-y-auto px-2 py-3 space-y-1">
        {q ? (
          filtered.length > 0 ? (
            <div className="space-y-0.5">{filtered.map((it) => renderItem(it, false))}</div>
          ) : (
            <p className="px-3 py-6 text-sm text-gray-400 text-center">ไม่พบเมนู “{query}”</p>
          )
        ) : (
          sections.map((section) => {
            const isCollapsed = !!collapsed[section.id];
            return (
              <div key={section.id} className="pt-1">
                <button
                  type="button"
                  onClick={() => toggleGroup(section.id)}
                  className="w-full flex items-center justify-between px-3 py-1.5 rounded text-xs font-semibold text-gray-500 uppercase tracking-wider hover:bg-gray-50"
                >
                  <span>{section.title}</span>
                  {isCollapsed ? (
                    <ChevronRight className="w-3.5 h-3.5 opacity-60" />
                  ) : (
                    <ChevronDown className="w-3.5 h-3.5 opacity-60" />
                  )}
                </button>
                {!isCollapsed && <div className="mt-0.5 space-y-0.5">{section.items.map((it) => renderItem(it))}</div>}
              </div>
            );
          })
        )}
      </nav>

      <div className="border-t border-gray-100 p-3">
        <button
          onClick={toggleRail}
          className="hidden lg:flex items-center gap-2 w-full px-2 py-1.5 mb-2 text-xs text-gray-500 hover:bg-gray-100 rounded-lg"
        >
          <PanelLeftClose className="w-4 h-4" /> ย่อเมนู
        </button>
        <div className="flex items-center gap-2 px-2 py-2 rounded-lg bg-gray-50">
          <div className="w-8 h-8 rounded-full bg-brand-gradient text-white flex items-center justify-center text-sm font-bold shrink-0">
            {(profile?.user.name ?? '?')[0]?.toUpperCase()}
          </div>
          <div className="min-w-0 flex-1">
            <div className="text-sm font-medium truncate">{profile?.user.name}</div>
            <div className="text-xs text-gray-500 truncate">{role}</div>
          </div>
          <button
            onClick={handleLogout}
            title="ออกจากระบบ"
            aria-label="ออกจากระบบ"
            className="p-1.5 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded"
          >
            <LogOut className="w-4 h-4" />
          </button>
        </div>
      </div>
    </aside>
  );

  const railSidebar = (
    <aside className="w-16 bg-white border-r border-gray-200 flex flex-col h-screen sticky top-0 items-center">
      <div className="py-4 border-b border-gray-100 w-full flex justify-center">
        <Logo />
      </div>
      <nav className="flex-1 overflow-y-auto py-3 w-full flex flex-col items-center gap-1">
        {allItems.map((it) => {
          const active = it.id === activeId;
          return (
            <Link
              key={it.id}
              to={`/${it.id}`}
              title={it.label}
              aria-label={it.label}
              className={`w-10 h-10 flex items-center justify-center rounded-lg transition ${
                active ? 'bg-brand-50 text-brand-700' : 'text-gray-600 hover:bg-gray-100'
              }`}
            >
              <Icon name={it.icon} className="w-5 h-5" />
            </Link>
          );
        })}
      </nav>
      <div className="border-t border-gray-100 p-2 w-full flex justify-center">
        <button
          onClick={toggleRail}
          title="ขยายเมนู"
          aria-label="ขยายเมนู"
          className="w-10 h-10 flex items-center justify-center rounded-lg text-gray-500 hover:bg-gray-100"
        >
          <PanelLeft className="w-5 h-5" />
        </button>
      </div>
    </aside>
  );

  return (
    <div className="min-h-screen bg-gray-50 flex">
      <div className="hidden lg:block shrink-0">{rail ? railSidebar : sidebar}</div>

      {drawerOpen && <div className="fixed inset-0 bg-black/40 z-30 lg:hidden" onClick={() => setDrawerOpen(false)} />}
      <div
        className={`fixed inset-y-0 left-0 z-40 lg:hidden transform transition-transform ${
          drawerOpen ? 'translate-x-0' : '-translate-x-full'
        }`}
      >
        {sidebar}
      </div>

      <main className="flex-1 min-w-0">
        <header className="bg-white border-b border-gray-200 sticky top-0 z-20">
          <div className="px-4 lg:px-8 py-3 flex items-center gap-3">
            <button onClick={() => setDrawerOpen(!drawerOpen)} className="p-2 -ml-2 rounded-lg hover:bg-gray-100 lg:hidden">
              {drawerOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
            </button>
            <button
              onClick={() => navigate(-1)}
              className="p-1 rounded-lg hover:bg-gray-100 hidden lg:block"
              title="ย้อนกลับ"
              aria-label="ย้อนกลับ"
            >
              <ChevronLeft className="w-5 h-5" />
            </button>
            <nav className="flex items-center gap-1 text-xs text-gray-400 truncate" aria-label="breadcrumb">
              <Link to="/" className="hover:text-gray-600 shrink-0">
                แผนผังระบบ
              </Link>
              {activeGroup && (
                <>
                  <ChevronRight className="w-3 h-3 shrink-0" />
                  <span className="shrink-0">{activeGroup.group.label}</span>
                </>
              )}
              {activeItem && (
                <>
                  <ChevronRight className="w-3 h-3 shrink-0" />
                  <span className="text-gray-600 truncate">{activeItem.label}</span>
                </>
              )}
            </nav>
            <div className="ml-auto shrink-0">
              <NotificationBell />
            </div>
          </div>
        </header>

        <div className="px-4 lg:px-8 py-4 lg:py-6 max-w-7xl">
          <SubNav />
          <ErrorBoundary key={location.pathname} level="page">
            <Suspense fallback={<div className="py-10"><Skeleton rows={6} /></div>}>
              <Outlet />
            </Suspense>
          </ErrorBoundary>
        </div>
      </main>
    </div>
  );
}
